import { ValueTypeSwitcher } from 'legacy/lut/components/widgets/public/partner360/common/valueTypeSwitcher/ValueTypeSwitcher'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Switch } from 'core-components/library/checkbox'
import { Tooltip } from 'core-components/library/tooltip'
import { useCurrencyRestriction } from 'partner360/components/currenciesButtons/hooks/useCurrencyRestriction'
import { CURRENCY_TRANSLATION_KEYS } from 'partner360/components/currenciesButtons/translations'
import { RowButtons } from 'partner360/components/rowButtons/RowButtons'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { PLANNING_SMB_TRANSLATION_KEYS } from '../../../../configs'

import styles from './PlanningSmbControls.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS.modal

/**
 * CheckboxBase toggles its input on every bubbled mouse-down, including clicks on controls
 * rendered by Translation in translation mode. Keep the translated label in Switch's native
 * layout, but isolate the translation editor so using it never changes the planning setting.
 */
const stopSwitchToggle = (event: ReactMouseEvent<HTMLSpanElement>) => event.stopPropagation()

const TranslationSwitchLabel = ({
  children,
  isTranslationEditMode,
}: {
  children: ReactNode
  isTranslationEditMode: boolean
}) => (
  <span
    onMouseDown={isTranslationEditMode ? stopSwitchToggle : undefined}
    onClick={isTranslationEditMode ? stopSwitchToggle : undefined}
  >
    {children}
  </span>
)

export interface IPlanningSmbControlsProps {
  isQuarterly: boolean
  /** Locked when the category-level Planning switch already enforces quarterly planning. */
  isQuarterlyDisabled: boolean
  showFinancialData: boolean
  baselineMetric: ActualsValueTypes
  baselineCurrency: string
  /** The same option lists Planning offers, so the popup never invents its own */
  actualsTypes: ActualsValueTypes[]
  currencies: string[]
  roundingValue: string
  roundingOptions: string[]
  disabled?: boolean
  onQuarterlyChange: (value: boolean) => void
  onShowFinancialDataChange: (value: boolean) => void
  onBaselineMetricChange: (value: ActualsValueTypes) => void
  onBaselineCurrencyChange: (value: string) => void
  onRoundingChange: (value: string) => void
}

export const PlanningSmbControls = ({
  isQuarterly,
  isQuarterlyDisabled,
  showFinancialData,
  baselineMetric,
  baselineCurrency,
  actualsTypes,
  currencies,
  roundingValue,
  roundingOptions,
  disabled,
  onQuarterlyChange,
  onShowFinancialDataChange,
  onBaselineMetricChange,
  onBaselineCurrencyChange,
  onRoundingChange,
}: IPlanningSmbControlsProps) => {
  const isTranslationEditMode = useSelector(selectTranslationMode) === TranslationMode.Edit
  const currencyRestriction = useCurrencyRestriction(baselineCurrency, onBaselineCurrencyChange)
  const currencyControl = (
    <RowButtons
      size="small"
      isDisabled={disabled || currencyRestriction.isMultiLocalCurrencyLocked}
      disableOptionTranslation
      value={currencyRestriction.currency}
      options={
        currencyRestriction.isMultiLocalCurrencyLocked && !currencies.includes('USD')
          ? ['USD', ...currencies]
          : currencies
      }
      generateTranslationKey={(value) => value}
      onOptionClick={(value) => {
        if (!disabled) currencyRestriction.changeCurrency(value)
      }}
    />
  )

  return (
    <div className={styles.controls}>
      <div className={styles.group}>
        <Switch
          inline
          checked={isQuarterly}
          disabled={disabled || isQuarterlyDisabled}
          label={
            <TranslationSwitchLabel isTranslationEditMode={isTranslationEditMode}>
              <Translation span text={KEYS.quarterlyPlanning} />
            </TranslationSwitchLabel>
          }
          onChange={(value) => onQuarterlyChange(!!value)}
        />
        <Switch
          inline
          checked={showFinancialData}
          disabled={disabled}
          label={
            <TranslationSwitchLabel isTranslationEditMode={isTranslationEditMode}>
              <Translation span text={KEYS.showFinancialData} />
            </TranslationSwitchLabel>
          }
          onChange={(value) => onShowFinancialDataChange(!!value)}
        />
      </div>

      {/* Metric/currency only affect the popup baseline; number scale is the shared Planning
          state so financial values always look exactly as they do behind the popup. */}
      <div className={styles.group}>
        <ValueTypeSwitcher
          size="small"
          valueType={baselineMetric}
          valueTypes={actualsTypes}
          onChangeValueType={onBaselineMetricChange}
        />
        {currencyRestriction.isMultiLocalCurrencyLocked ? (
          <Tooltip
            inline
            tipContent={() => (
              <Translation text={CURRENCY_TRANSLATION_KEYS.multipleLocalCurrencies} />
            )}
          >
            {currencyControl}
          </Tooltip>
        ) : (
          currencyControl
        )}
        <RowButtons
          size="small"
          isDisabled={disabled}
          value={roundingValue}
          options={roundingOptions}
          generateTranslationKey={(value) => value}
          onOptionClick={onRoundingChange}
        />
      </div>

      <div className={styles.rule}>
        <Translation span text={KEYS.hierarchyRule} />
      </div>
    </div>
  )
}
